'use client';

import { usePathname } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { useSession } from 'next-auth/react';
import { ProfileControl } from '@owlcat/identity-react';
import { accountsProfileUrl, publicOrigin } from '@/shared/utils/publicOrigin';

/**
 * PortalProfileControl — portal's thin wrapper over the fleet-shared
 * @owlcat/identity-react <ProfileControl>, so the portal header shows the SAME
 * "logged in as X / Sign in" control as the landing + scaffold apps.
 *
 * It is a CLIENT widget: it resolves the current identity itself via a
 * credentialed cross-subdomain fetch to accounts `/me` (parent-domain owlcat-id
 * cookie). It is ADDITIVE — portal's own NextAuth session logic is unchanged;
 * this only swaps the header's bespoke profile/sign-in UI for the shared one.
 * Sign-out POSTs accounts `/logout` (bumps session_epoch + clears the shared
 * cookie); portal's epoch kill-switch then revokes the native session.
 */

const stripSlash = (u: string) => u.replace(/\/+$/, '');

/** Browser-reachable accounts origin. Prefer NEXT_PUBLIC_ACCOUNTS_URL; dev loopback fallback. */
function publicAccountsUrl(): string {
  return stripSlash(process.env.NEXT_PUBLIC_ACCOUNTS_URL || 'http://127.0.0.1:3200');
}

/** The "your apps" hub — the apex landing (parent domain), locale-matched. */
function appsHubUrl(locale: string): string {
  const parent = process.env.NEXT_PUBLIC_OWLCAT_PARENT_DOMAIN?.trim().replace(/^\./, '');
  return parent ? `https://${parent}/${locale}/` : '/';
}

export function PortalProfileControl() {
  const locale = useLocale();
  const t = useTranslations('common');
  const pathname = usePathname();
  const { data: session } = useSession();
  const su = session?.user as { identityId?: string; name?: string | null; email?: string | null; image?: string | null } | undefined;

  const accounts = publicAccountsUrl();
  /**
   * The origin comes from the build, not from `window`.
   *
   * Reading `window.location.origin` during render is a server/client branch:
   * the HTML went out with `return=` empty and the browser rendered the real
   * URL, which React reports as a hydration mismatch and does NOT patch up —
   * so the link stayed broken. See `publicOrigin`.
   */
  const origin = publicOrigin();
  // Where accounts sends the user back to after login/logout — current portal URL.
  const returnTo = origin ? `${origin}${pathname}` : undefined;
  // accounts /profile deep-link: locale-matched + validated back-to-portal button.
  const profileUrl = accountsProfileUrl({ locale });

  return (
    <ProfileControl
      locale={locale}
      accountsUrl={accounts}
      appId="portal"
      selfPath="/me"
      loginPath="/login"
      logoutPath="/logout"
      profileUrl={profileUrl}
      appsUrl={appsHubUrl(locale)}
      returnTo={returnTo}
      /**
       * Signing in is the quiet way back, not the thing to do.
       *
       * A filled button in the corner competed with the one call to action the
       * page is built around — on the campaign landing it sat next to "Создать
       * сказку бесплатно" and pulled the eye first, which is the wrong first
       * click for somebody who has no account yet. The hub settled this the
       * same way.
       */
      signInVariant="outline"
      initialProfile={su?.identityId ? { identityId: su.identityId, name: su.name ?? null, email: su.email ?? null, avatar: su.image ?? null } : null}
      labels={{
        signIn: t('signIn'),
        signOut: t('signOut'),
        profile: t('profile'),
        yourApps: t('yourApps'),
        account: t('account'),
      }}
    />
  );
}
